import React, { useMemo } from 'react'
import { Timeline, Empty, Avatar, Card, Descriptions } from 'antd'
import { Vertical, Horizontal, MaterialIcon, Texto, BBDTag, addCommasToNumber } from '@gravitate-js/excalibrr'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import moment from 'moment'
import { formatters } from '@utils/intl/dateTimeFormatters'
import type { components } from '../../../../types/api/v1'
import type { MatchDetailResponse } from '../../../../api/useSupplierReconciliation/useMatchDetail'
import { getUserTimezone } from '@utils/intl/dateTimeFormatters'
import { useFormatter } from '@api/useFormatter'

type SRActivity = components['schemas']['SRActivity']

dayjs.extend(relativeTime)
dayjs.extend(utc)
dayjs.extend(timezone)

interface MatchHistoryContainerProps {
  history?: SRActivity[]
  match?: MatchDetailResponse
}

export function MatchHistoryContainer({ history, match }: MatchHistoryContainerProps) {
  const { formatCurrency } = useFormatter()
  const currencySymbol = formatCurrency(null, true)

  if (!history || history.length === 0) {
    return (
      <Vertical bg='elevated' style={{ height: '100%', padding: '16px' }} verticalCenter horizontalCenter>
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={<Texto appearance='hint'>No history available for this match</Texto>}
        />
      </Vertical>
    )
  }

  function getActionIcon(activityType: string): string {
    if (!activityType) return 'history'
    switch (activityType) {
      case 'invoice_added':
        return 'add_circle'
      case 'invoice_removed':
        return 'remove_circle'
      case 'bol_added':
        return 'description'
      case 'bol_removed':
        return 'cancel'
      case 'invoice_voided':
        return 'block'
      case 'invoice_unvoided':
        return 'check_circle'
      case 'match_approved':
        return 'check_circle'
      case 'hold_set':
        return 'pause_circle'
      case 'hold_removed':
        return 'play_circle'
      case 'status_changed':
        return 'sync_alt'
      case 'match_updated':
        return 'update'
      case 'documents_updated':
        return 'edit_document'
      case 'match_linked':
        return 'link'
      case 'match_unlinked':
        return 'link_off'
      case 'document_unlinked':
        return 'link_off'
      case 'document_linked':
        return 'link'
      case 'match_split':
        return 'call_split'
      case 'match_merged':
        return 'call_merge'
      case 'comment':
        return 'notifications'
      default:
        return 'history'
    }
  }

  function getActionColor(activityType: string): string {
    if (!activityType) return 'var(--theme-text-secondary)'
    switch (activityType) {
      case 'invoice_added':
      case 'bol_added':
        return 'var(--theme-color-2)'
      case 'invoice_removed':
      case 'bol_removed':
        return 'var(--theme-error)'
      case 'invoice_voided':
        return 'var(--theme-error)'
      case 'invoice_unvoided':
        return 'var(--theme-color-2)'
      case 'match_approved':
        return 'var(--theme-success)'
      case 'hold_set':
        return 'var(--theme-warning)'
      case 'hold_removed':
        return 'var(--theme-color-2)'
      case 'status_changed':
        return 'var(--theme-color-1)'
      case 'match_updated':
        return 'var(--theme-color-1)'
      case 'documents_updated':
        return 'var(--theme-color-1)'
      case 'match_linked':
        return 'var(--theme-success)'
      case 'match_unlinked':
        return 'var(--theme-warning)'
      case 'document_unlinked':
        return 'var(--theme-error)'
      case 'document_linked':
        return 'var(--theme-success)'
      case 'match_split':
        return 'var(--theme-warning)'
      case 'match_merged':
        return 'var(--theme-success)'
      case 'comment':
        return 'var(--theme-text-secondary)'
      default:
        return 'var(--theme-text-secondary)'
    }
  }

  function getActionTag(activityType: string) {
    if (!activityType) return <BBDTag style={{ fontSize: '10px' }}>UNKNOWN</BBDTag>
    switch (activityType) {
      case 'invoice_added':
        return (
          <BBDTag theme2 style={{ fontSize: '10px' }}>
            INVOICE ADDED
          </BBDTag>
        )
      case 'invoice_removed':
        return (
          <BBDTag error style={{ fontSize: '10px' }}>
            INVOICE REMOVED
          </BBDTag>
        )
      case 'bol_added':
        return (
          <BBDTag theme2 style={{ fontSize: '10px' }}>
            BOL ADDED
          </BBDTag>
        )
      case 'bol_removed':
        return (
          <BBDTag error style={{ fontSize: '10px' }}>
            BOL REMOVED
          </BBDTag>
        )
      case 'invoice_voided':
        return (
          <BBDTag error style={{ fontSize: '10px' }}>
            VOIDED
          </BBDTag>
        )
      case 'invoice_unvoided':
        return (
          <BBDTag theme2 style={{ fontSize: '10px' }}>
            UNVOIDED
          </BBDTag>
        )
      case 'match_approved':
        return (
          <BBDTag success style={{ fontSize: '10px' }}>
            APPROVED
          </BBDTag>
        )
      case 'hold_set':
        return (
          <BBDTag warning style={{ fontSize: '10px' }}>
            ON HOLD
          </BBDTag>
        )
      case 'hold_removed':
        return (
          <BBDTag theme2 style={{ fontSize: '10px' }}>
            HOLD REMOVED
          </BBDTag>
        )
      case 'status_changed':
        return (
          <BBDTag theme2 style={{ fontSize: '10px' }}>
            STATUS CHANGED
          </BBDTag>
        )
      case 'match_updated':
        return (
          <BBDTag theme2 style={{ fontSize: '10px' }}>
            MATCH UPDATED
          </BBDTag>
        )
      case 'documents_updated':
        return (
          <BBDTag theme2 style={{ fontSize: '10px' }}>
            DOCUMENTS UPDATED
          </BBDTag>
        )
      case 'match_linked':
        return (
          <BBDTag success style={{ fontSize: '10px' }}>
            LINKED
          </BBDTag>
        )
      case 'match_unlinked':
        return (
          <BBDTag warning style={{ fontSize: '10px' }}>
            UNLINKED
          </BBDTag>
        )
      case 'document_unlinked':
        return (
          <BBDTag error style={{ fontSize: '10px' }}>
            DOCUMENT UNLINKED
          </BBDTag>
        )
      case 'document_linked':
        return (
          <BBDTag success style={{ fontSize: '10px' }}>
            DOCUMENT LINKED
          </BBDTag>
        )
      case 'match_split':
        return (
          <BBDTag warning style={{ fontSize: '10px' }}>
            MATCH SPLIT
          </BBDTag>
        )
      case 'match_merged':
        return (
          <BBDTag success style={{ fontSize: '10px' }}>
            MATCH MERGED
          </BBDTag>
        )
      case 'comment':
        return <BBDTag style={{ fontSize: '10px' }}>COMMENT</BBDTag>
      default:
        return <BBDTag style={{ fontSize: '10px' }}>{activityType.replace(/_/g, ' ').toUpperCase()}</BBDTag>
    }
  }

  function getActionText(activityType: string): string {
    if (!activityType) return 'Unknown Activity'
    switch (activityType) {
      case 'invoice_added':
        return 'Invoice Added'
      case 'invoice_removed':
        return 'Invoice Removed'
      case 'bol_added':
        return 'BOL Added'
      case 'bol_removed':
        return 'BOL Removed'
      case 'invoice_voided':
        return 'Invoice Voided'
      case 'invoice_unvoided':
        return 'Invoice Unvoided'
      case 'match_approved':
        return 'Match Approved'
      case 'hold_set':
        return 'Hold Set'
      case 'hold_removed':
        return 'Hold Removed'
      case 'status_changed':
        return 'Status Changed'
      case 'match_updated':
        return 'Match Updated'
      case 'documents_updated':
        return 'Documents Updated'
      case 'match_linked':
        return 'Match Linked'
      case 'match_unlinked':
        return 'Match Unlinked'
      case 'document_unlinked':
        return 'Document Unlinked'
      case 'document_linked':
        return 'Document Linked'
      case 'match_split':
        return 'Match Split'
      case 'match_merged':
        return 'Match Merged'
      default:
        return activityType.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())
    }
  }

  function formatExtraData(extraData: unknown): string {
    if (!extraData || typeof extraData !== 'object') return ''

    const data = extraData as Record<string, unknown>

    // Handle specific known extra data fields
    if (data.invoice_number && typeof data.invoice_number === 'string') {
      return `Invoice: ${data.invoice_number}`
    }
    if (data.bol_number && typeof data.bol_number === 'string') {
      return `BOL: ${data.bol_number}`
    }
    if (data.reason && typeof data.reason === 'string') {
      return `Reason: ${data.reason}`
    }
    if (data.notes && typeof data.notes === 'string') {
      return `Notes: ${data.notes}`
    }

    // For other data, try to format nicely
    const entries = Object.entries(data)
    if (entries.length === 0) return ''

    return entries
      .map(([key, value]) => {
        // Format numeric values that might be amounts or volumes
        if (typeof value === 'number') {
          if (key.includes('amount') || key.includes('total') || key.includes('cost')) {
            return `${key.replace(/_/g, ' ')}: ${currencySymbol}${addCommasToNumber(value, 2)}`
          }
          if (key.includes('volume') || key.includes('gallons')) {
            return `${key.replace(/_/g, ' ')}: ${addCommasToNumber(value, 2)} gal`
          }
          return `${key.replace(/_/g, ' ')}: ${addCommasToNumber(value, 2)}`
        }
        return `${key.replace(/_/g, ' ')}: ${value}`
      })
      .join(', ')
  }

  // Sort history by timestamp (newest first for timeline display)
  const sortedHistory = [...history].sort((a, b) => dayjs(b.created_on).unix() - dayjs(a.created_on).unix())

  const timelineItems = sortedHistory.map((entry) => ({
    dot: (
      <Avatar
        size={32}
        style={{
          background: getActionColor(entry.activity_type),
          boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
        }}
        icon={<MaterialIcon icon={getActionIcon(entry.activity_type)} size={16} style={{ color: 'white' }} />}
      />
    ),
    children: (
      <Card
        size='small'
        style={{
          marginBottom: 12,
          border: '1px solid var(--border-color)',
          borderRadius: '8px',
        }}
        bodyStyle={{ padding: '8px' }}
        title={
          <Horizontal gap={8} verticalCenter justifyContent='space-between' style={{ flexWrap: 'nowrap' }}>
            <div style={{ flex: '0 0 auto', minWidth: 0 }}>{getActionTag(entry.activity_type)}</div>
            <Texto
              category='label'
              appearance='hint'
              style={{ flex: '0 0 auto', minWidth: 'fit-content', whiteSpace: 'nowrap' }}
            >
              {dayjs(entry.created_on).fromNow().replace('a few seconds ago', 'seconds ago')}
            </Texto>
          </Horizontal>
        }
      >
        {/* Extra data in a clean format */}
        {entry.extra_data && Object.keys(entry.extra_data).length > 0 && (
          <Vertical gap={4} style={{ marginBottom: '8px' }}>
            {entry.extra_data.invoice_number && (
              <Horizontal gap={4} verticalCenter>
                <Texto category='p2' appearance='secondary'>
                  Invoice:
                </Texto>
                <Texto category='p2' weight='medium'>
                  {entry.extra_data.invoice_number}
                </Texto>
              </Horizontal>
            )}
            {entry.extra_data.bol_number && (
              <Horizontal gap={4} verticalCenter>
                <Texto category='p2' appearance='secondary'>
                  BOL:
                </Texto>
                <Texto category='p2' weight='medium'>
                  {entry.extra_data.bol_number}
                </Texto>
              </Horizontal>
            )}
            {entry.extra_data.reason && (
              <Horizontal gap={4} verticalCenter>
                <Texto category='p2' appearance='secondary'>
                  Reason:
                </Texto>
                <Texto category='p2'>{entry.extra_data.reason}</Texto>
              </Horizontal>
            )}
            {entry.extra_data.notes && (
              <Horizontal gap={4} verticalCenter>
                <Texto category='p2' appearance='secondary'>
                  Notes:
                </Texto>
                <Texto category='p2'>{entry.extra_data.notes}</Texto>
              </Horizontal>
            )}
            {entry.extra_data.from_status && entry.extra_data.to_status && (
              <Horizontal gap={4} verticalCenter>
                <Texto category='p2' appearance='secondary'>
                  Status changed:
                </Texto>
                <Texto category='p2' weight='medium'>
                  {entry.extra_data.from_status} → {entry.extra_data.to_status}
                </Texto>
              </Horizontal>
            )}
            {entry.extra_data.action_description && (
              <Horizontal gap={4} verticalCenter>
                <Texto category='p2' weight='medium'>
                  {entry.extra_data.action_description}
                </Texto>
              </Horizontal>
            )}
          </Vertical>
        )}

        {/* Footer with user and timestamp */}
        <Vertical gap={4} style={{ borderTop: '1px solid var(--border-color)', paddingTop: '8px' }}>
          <Horizontal gap={4} verticalCenter>
            <MaterialIcon icon='person' size={14} style={{ color: 'var(--theme-text-hint)' }} />
            <Texto category='p2' appearance='hint'>
              {entry.username === 'Auto Rec' ? 'System' : entry.username}
            </Texto>
          </Horizontal>
          <Horizontal gap={4} verticalCenter>
            <MaterialIcon icon='schedule' size={14} style={{ color: 'var(--theme-text-hint)' }} />
            <Texto category='p2' appearance='hint'>
              {formatters.WEEKDAY_MONTH_DATE_TIME(
                moment.utc(entry.created_on),
                match?.bol_date_local || match?.timezone_data?.terminal,
                false
              )}
            </Texto>
          </Horizontal>
        </Vertical>
      </Card>
    ),
  }))

  return (
    <>
      <style>
        {`
          .custom-timeline .ant-timeline-item-head {
            background-color: transparent !important;
            border: none !important;
          }
          .custom-timeline .ant-timeline-item-head-custom {
            background-color: transparent !important;
            border: none !important;
          }
        `}
      </style>
      <Vertical bg='elevated' style={{ height: '100%', padding: '16px' }}>
        <div
          style={{
            flex: 1,
            overflowY: 'auto',
            overflowX: 'hidden',
          }}
        >
          <Timeline
            mode='left'
            items={timelineItems}
            style={{ marginTop: 0, paddingLeft: '16px', paddingTop: '16px' }}
            className='custom-timeline'
          />
        </div>
      </Vertical>
    </>
  )
}
